import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';
import { Button } from '../../styles/components';
import { useCart } from '../../context/CartContext';

const SummaryContainer = styled.div`
  background-color: ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.small};
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0 -${theme.spacing.sm};
    box-shadow: ${theme.shadows.medium};
  }
`;

const SummaryTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.gray};
  padding-bottom: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
    margin-bottom: ${theme.spacing.md};
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  font-size: 1rem;
`;

const TotalRow = styled(SummaryRow)`
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.gray};
  padding-top: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    margin-top: ${theme.spacing.sm};
    padding-top: ${theme.spacing.sm};
  }
`;

const CheckoutButton = styled(Button)`
  width: 100%;
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 1.125rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: ${theme.spacing.md};
    font-size: 1rem;
  }
`;

const EmptyCart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${theme.spacing.xl} 0;
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.gray};
    margin-bottom: ${theme.spacing.md};
  }
  
  h3 {
    margin-bottom: ${theme.spacing.md};
  }
  
  p {
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.darkGray};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg} 0;
    
    svg {
      font-size: 2.5rem;
      margin-bottom: ${theme.spacing.sm};
    }
    
    h3 {
      margin-bottom: ${theme.spacing.sm};
    }
    
    p {
      margin-bottom: ${theme.spacing.md};
      font-size: 0.9rem;
    }
  }
`;

const CartSummary: React.FC = () => {
  const { items, totalItems, totalPrice } = useCart();
  
  const shippingPrice = totalPrice >= 100 ? 0 : 10;
  const finalTotal = totalPrice + shippingPrice;
  
  if (items.length === 0) {
    return (
      <SummaryContainer>
        <EmptyCart>
          <FontAwesomeIcon icon={faShoppingBag} />
          <h3>Your cart is empty</h3>
          <p>Looks like you haven't added any items to your cart yet.</p>
          <Button as={Link} to="/products">
            Continue Shopping
          </Button>
        </EmptyCart>
      </SummaryContainer>
    );
  }
  
  return (
    <SummaryContainer>
      <SummaryTitle>Order Summary</SummaryTitle>
      <SummaryRow>
        <span>Items ({totalItems})</span>
        <span>${totalPrice.toFixed(2)}</span>
      </SummaryRow>
      <SummaryRow>
        <span>Shipping</span>
        <span>
          {shippingPrice === 0 ? 'Free' : `$${shippingPrice.toFixed(2)}`}
        </span>
      </SummaryRow>
      <TotalRow>
        <span>Total</span>
        <span>${finalTotal.toFixed(2)}</span>
      </TotalRow>
      <CheckoutButton as={Link} to="/checkout">
        Proceed to Checkout
      </CheckoutButton>
    </SummaryContainer>
  );
};

export default CartSummary; 